export class NetworkError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
  ) {
    super(message);
    this.name = "NetworkError";
  }
}

export const fetchWithRetry = async <T>(
  fetchFn: () => Promise<T>,
  options: {
    maxRetries?: number;
    retryDelay?: number;
    shouldRetry?: (error: unknown) => boolean;
  } = {},
): Promise<T> => {
  const {
    maxRetries = 3,
    retryDelay = 1000,
    shouldRetry = (error) => {
      const e = error as { status?: number | string };
      return (
        e?.status === "FETCH_ERROR" ||
        (typeof e?.status === "number" && e.status >= 500) ||
        e?.status === undefined
      );
    },
  } = options;

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error: unknown) {
      lastError = error;
      const e = error as { status?: number };

      if (
        e?.status === 400 ||
        e?.status === 401 ||
        e?.status === 403 ||
        e?.status === 404
      ) {
        throw error;
      }

      if (attempt < maxRetries && shouldRetry(error)) {

        const delay = retryDelay * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      break;
    }
  }

  throw lastError;
};

export const isNetworkError = (error: unknown): boolean => {
  const e = error as { status?: string; name?: string; message?: string };
  return !!(
    e?.status === "FETCH_ERROR" ||
    e?.name === "TypeError" ||
    e?.message?.includes("Failed to fetch") ||
    e?.message?.includes("Network request failed")
  );
};
